import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isSessionRequiredError } from '../../api/client.js';
import type { TenantAuthContext } from '../../domain/models.js';
import { repository } from '../../repositories/index.js';

export type AuthSessionStatus = 'initial' | 'mock' | 'restoring' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthSessionValue {
  status: AuthSessionStatus;
  context: TenantAuthContext | null;
  acceptAuthenticated: (context: TenantAuthContext) => void;
  expireSession: () => void;
  clearSession: () => void;
}

const AuthSessionContext = React.createContext<AuthSessionValue | null>(null);

export function AuthSessionProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [status, setStatus] = React.useState<AuthSessionStatus>(repository.mode === 'mock' ? 'mock' : 'initial');
  const [context, setContext] = React.useState<TenantAuthContext | null>(repository.getCachedAuthContext());

  React.useEffect(() => {
    if (repository.mode !== 'api' || status !== 'initial') return;
    if (!location.pathname.startsWith('/tenant/')) {
      setStatus('unauthenticated');
      return;
    }
    let active = true;
    setStatus('restoring');
    repository.restoreAuthContext().then((restored) => {
      if (!active) return;
      setContext(restored);
      setStatus('authenticated');
    }).catch((error: unknown) => {
      if (!active) return;
      setContext(null);
      setStatus(isSessionRequiredError(error) ? 'unauthenticated' : 'error');
    });
    return () => { active = false; };
  }, [location.pathname, status]);

  const value = React.useMemo<AuthSessionValue>(() => ({
    status,
    context,
    acceptAuthenticated(nextContext) {
      setContext(nextContext);
      setStatus('authenticated');
    },
    expireSession() {
      setContext(null);
      setStatus('unauthenticated');
    },
    clearSession() {
      setContext(null);
      setStatus(repository.mode === 'mock' ? 'mock' : 'unauthenticated');
    },
  }), [context, status]);

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

export function useAuthSession(): AuthSessionValue {
  const value = React.useContext(AuthSessionContext);
  if (!value) throw new Error('AuthSessionProvider is required.');
  return value;
}

export function RequireApiSession({ children }: { children: React.ReactNode }) {
  const session = useAuthSession();
  if (repository.mode === 'mock') return <>{children}</>;
  if (session.status === 'restoring' || session.status === 'initial') {
    return <main className="auth-page"><section className="auth-card"><span className="brand auth-brand">DataSota</span><p className="eyebrow">СЕССИЯ</p><h1>Проверяем контекст</h1><p>Восстанавливаем серверную сессию DataSota.</p></section></main>;
  }
  if (session.status === 'authenticated') return <>{children}</>;
  return <Navigate to="/auth/error?reason=session-expired" replace />;
}
